import type { ButtonOrLinkOwnProps } from '#/components/ButtonOrLink/ButtonOrLink';
import type { ButtonOrLinkDataProps } from '#/components/ButtonOrLink/ButtonOrLinkData';
import { Icon } from '#/components/Icon/Icon';
import { getDataAndAriaProps } from '#/utils/getDataAndAriaProps';

import { ButtonIconWrapper, StyledButtonOrLink } from './Button.styled';
import type { ButtonIcon, ButtonSize, ButtonVariant } from './Button.types';

export type ButtonOwnProps = ButtonOrLinkOwnProps & {
  icon?: ButtonIcon;
  size?: ButtonSize;
  variant: ButtonVariant;
  width?: number;
  fullWidth?: boolean;
  height?: number;
  noPadding?: boolean;
  noBold?: boolean;
  rounded?: boolean;
};

type Props = ButtonOrLinkDataProps<ButtonOwnProps>;

export function Button({
  id,
  className,
  children,
  icon,
  size,
  tabIndex,
  variant,
  width,
  fullWidth,
  height,
  noPadding,
  noBold,
  title,
  rounded,
  qaId,
  style,
  ...otherProps
}: Props) {
  const data = 'data' in otherProps ? otherProps.data : otherProps;
  const hideObjectIcon =
    Boolean(children) ||
    Boolean('aria-label' in otherProps && otherProps['aria-label']);

  return (
    <StyledButtonOrLink
      data={data}
      id={id}
      className={[
        className,
        variant,
        size ?? 'medium',
        fullWidth && 'full-width',
        noPadding && 'no-padding',
        noBold && 'no-bold',
        rounded && 'rounded',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ width, height, ...style }}
      title={title}
      tabIndex={tabIndex}
      qaId={qaId}
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...getDataAndAriaProps(otherProps)}
    >
      {icon && (
        <ButtonIconWrapper
          $position={
            (typeof icon === 'object' && 'position' in icon && icon.position) ||
            'left'
          }
        >
          {typeof icon === 'object' && 'name' in icon ? (
            <Icon
              aria-hidden={hideObjectIcon || undefined}
              colorName={icon.color}
              name={icon.name}
              size={icon.size ?? 20}
            />
          ) : (
            icon
          )}
        </ButtonIconWrapper>
      )}

      {children && <div style={{ display: 'inline-flex' }}>{children}</div>}
    </StyledButtonOrLink>
  );
}
